(() => {
  'use strict';

  // Update-safe free shipping sync (no theme core edits).
  const SELECTORS = ['#threshold_bar_popup_minicart', '#threshold_bar_popup'];
  const OBSERVE_SELECTOR = SELECTORS.join(',');
  const CART_ENDPOINT_RE = /\/cart\/(add|change|update|clear)\.js|\/cart\.js/i;
  const VIEW_ENDPOINT_RE = /\/cart\?view=(?:up_ajax|ship)/i;

  const ZERO_DECIMAL_CURRENCIES = new Set([
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'JPY',
    'KMF',
    'KRW',
    'MGA',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF'
  ]);

  let refreshTimer = null;
  let inFlight = null;
  let lastSignature = '';
  let lastCart = null;
  let suppressFetchHook = false;

  const getTheme = () => (typeof window !== 'undefined' ? window.theme : null) || {};

  const getMoneyFormat = () => {
    const theme = getTheme();
    return theme.moneyFormat || (window.Shopify && Shopify.money_format) || '${{amount}}';
  };

  const getCurrencyCode = () => {
    if (window.Shopify && Shopify.currency) {
      const cur = Shopify.currency.active || Shopify.currency;
      if (cur) return String(cur).toUpperCase();
    }
    if (lastCart && lastCart.currency) return String(lastCart.currency).toUpperCase();
    if (window.shopCurrency) return String(window.shopCurrency).toUpperCase();
    return null;
  };

  const getMinorUnit = () => {
    const code = getCurrencyCode();
    if (code && ZERO_DECIMAL_CURRENCIES.has(code)) return 1;
    return 100;
  };

  const getThresholdValue = () => {
    const theme = getTheme();
    const raw = theme.freeshipping_value;
    if (raw !== undefined && raw !== null && raw !== '') {
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    }
    const el = document.querySelector('[data-free-shipping-threshold]');
    if (el) {
      const num = Number(el.getAttribute('data-free-shipping-threshold'));
      return Number.isFinite(num) ? num : null;
    }
    return null;
  };

  const formatMoney = (cents) => {
    const format = getMoneyFormat();
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    const factor = getMinorUnit();
    const amount = (cents / factor).toFixed(factor === 1 ? 0 : 2);
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  const setProgressWidth = (el, width) => {
    if (!el) return;
    const prevTransition = el.style.transition;
    el.style.transition = 'none';
    el.style.setProperty('width', `${width}%`, 'important');
    void el.offsetWidth;
    el.style.transition = prevTransition || '';
  };

  const getState = (cart) => {
    const thresholdValue = getThresholdValue();
    if (!thresholdValue || !Number.isFinite(thresholdValue) || thresholdValue <= 0) return null;
    const factor = getMinorUnit();
    const thresholdCents = Math.round(thresholdValue * factor);
    const totalCents = cart && Number.isFinite(cart.total_price) ? cart.total_price : 0;
    const isFree = totalCents >= thresholdCents;
    const remainingCents = Math.max(0, thresholdCents - totalCents);
    const percent = thresholdCents > 0
      ? Math.min(100, Math.max(0, Math.round((totalCents / thresholdCents) * 100)))
      : 0;
    return { thresholdCents, totalCents, isFree, remainingCents, percent };
  };

  const applyStateToParts = (spend, bar, animate, state, theme) => {
    if (!spend || !bar || !animate || !state) return;
    if (state.isFree) {
      spend.classList.add('spend_congrats');
      bar.classList.add('threshold_congrats');
      spend.innerHTML =
        (theme.strings && theme.strings.content_threshold
          ? theme.strings.content_threshold
          : "You've got free shipping!") + '<i class="rbb-icon-delivery-11"></i>';
      setProgressWidth(animate, 100);
    } else {
      spend.classList.remove('spend_congrats');
      bar.classList.remove('threshold_congrats');
      spend.innerHTML = `${theme.strings && theme.strings.spend ? theme.strings.spend : 'Spend'} ${formatMoney(state.remainingCents)} ${theme.strings && theme.strings.spend__html ? theme.strings.spend__html : 'for Free Shipping'}`;
      setProgressWidth(animate, state.percent);
    }
  };

  const applyStateToContainer = (container, state) => {
    if (!container || !state) return false;
    const theme = getTheme();
    let didUpdate = false;

    const thresholdNodes = container.querySelectorAll('.cart_threshold');
    if (thresholdNodes.length) {
      thresholdNodes.forEach((node) => {
        const spend = node.querySelector('.threshold_spend');
        const bar = node.querySelector('.threshold_bar');
        const animate = node.querySelector('.threshold_bar .animate');
        applyStateToParts(spend, bar, animate, state, theme);
        didUpdate = true;
      });
      return didUpdate;
    }

    const spend = container.querySelector('.threshold_spend');
    const bar = container.querySelector('.threshold_bar');
    const animate = container.querySelector('.threshold_bar .animate');
    if (spend && bar && animate) {
      applyStateToParts(spend, bar, animate, state, theme);
      return true;
    }

    return false;
  };

  const buildMarkup = ({ remainingCents, percent, isFree }) => {
    const theme = getTheme();
    if (isFree) {
      const text = theme.strings && theme.strings.content_threshold
        ? theme.strings.content_threshold
        : "You've got free shipping!";
      return `
        <div class="cart_threshold">
          <div class="threshold_spend spend_congrats"><span>${text}<i class="rbb-icon-delivery-11"></i></span></div>
          <div class="threshold_bar threshold_congrats">
            <span class="animate" style="width: 100% !important;">
              <span><i class="rbb-icon-delivery-11"></i></span>
            </span>
          </div>
        </div>
      `;
    }

    const spend = formatMoney(remainingCents);
    const spendPrefix = theme.strings && theme.strings.spend ? theme.strings.spend : 'Spend';
    const spendSuffix = theme.strings && theme.strings.spend__html ? theme.strings.spend__html : 'for Free Shipping';

    return `
      <div class="cart_threshold">
        <div class="threshold_spend">${spendPrefix} ${spend} ${spendSuffix}</div>
        <div class="threshold_bar">
          <span class="animate" style="width: ${percent}%!important">
            <span><i class="rbb-icon-delivery-11"></i></span>
          </span>
        </div>
      </div>
    `;
  };

  const updateThresholdElements = (cart) => {
    const state = getState(cart);
    if (!state) return;

    const theme = getTheme();
    if (theme.show_free_shipping === false) {
      SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => el.classList.add('d-none'));
      });
      return;
    }

    const signature = `${state.thresholdCents}|${state.totalCents}|${state.isFree}|${state.percent}`;
    const targets = SELECTORS.flatMap((selector) => Array.from(document.querySelectorAll(selector)));
    if (
      signature === lastSignature &&
      targets.length > 0 &&
      targets.every((el) => el.dataset.fsSignature === signature)
    ) {
      return;
    }
    lastSignature = signature;

    const markup = buildMarkup({
      remainingCents: state.remainingCents,
      percent: state.percent,
      isFree: state.isFree
    });

    targets.forEach((el) => {
      el.classList.remove('d-none');

      if (!applyStateToContainer(el, state)) {
        el.innerHTML = markup;
      }

      el.dataset.fsSignature = signature;
    });
  };

  const patchViewResponse = (html) => {
    if (!html || typeof html !== 'string') return html;
    if (!lastCart) return html;
    const state = getState(lastCart);
    if (!state) return html;
    try {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      if (applyStateToContainer(wrapper, state)) {
        return wrapper.innerHTML;
      }
    } catch (err) {}
    return html;
  };

  const hookJqueryViewResponses = () => {
    const $ = window.jQuery;
    if (!$ || $.__fsViewPatched) return;

    const originalGet = $.get;
    $.get = function patchedGet(url, data, success, dataType) {
      let actualUrl = url;
      let actualData = data;
      let actualSuccess = success;
      let actualDataType = dataType;
      if (typeof data === 'function') {
        actualSuccess = data;
        actualData = undefined;
        actualDataType = success;
      }
      if (typeof actualSuccess === 'function' && VIEW_ENDPOINT_RE.test(actualUrl)) {
        const wrapped = function (response, status, xhr) {
          const patched = patchViewResponse(response);
          return actualSuccess.call(this, patched, status, xhr);
        };
        return originalGet.call(this, actualUrl, actualData, wrapped, actualDataType);
      }
      return originalGet.apply(this, arguments);
    };

    if (typeof $.ajaxPrefilter === 'function') {
      $.ajaxPrefilter((options) => {
        if (!options || !options.url || !VIEW_ENDPOINT_RE.test(options.url)) return;
        const originalFilter = options.dataFilter;
        options.dataFilter = function (data, type) {
          const patched = patchViewResponse(data);
          return typeof originalFilter === 'function' ? originalFilter(patched, type) : patched;
        };
        if (typeof options.success === 'function') {
          const originalSuccess = options.success;
          options.success = function (response, status, xhr) {
            return originalSuccess.call(this, patchViewResponse(response), status, xhr);
          };
        }
      });
    }

    $.__fsViewPatched = true;
  };

  const fetchCart = () => {
    if (inFlight) return inFlight;
    suppressFetchHook = true;
    inFlight = fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null)
      .then((cart) => {
        if (cart) {
          lastCart = cart;
          updateThresholdElements(cart);
        }
        return cart;
      })
      .finally(() => {
        inFlight = null;
        setTimeout(() => {
          suppressFetchHook = false;
        }, 0);
      });
    return inFlight;
  };

  const queueRefresh = (delay = 150) => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      fetchCart();
    }, delay);
  };

  const isCartRequest = (url) => {
    if (!url) return false;
    return CART_ENDPOINT_RE.test(url);
  };

  const hookFetch = () => {
    if (!window.fetch) return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = (...args) => {
      const request = args[0];
      const url = typeof request === 'string' ? request : request && request.url;
      const responsePromise = originalFetch(...args);
      if (!suppressFetchHook && isCartRequest(url)) {
        responsePromise
          .then((response) => {
            try {
              const clone = response && response.clone ? response.clone() : null;
              if (clone) {
                return clone.json().then((cart) => {
                  if (cart && Number.isFinite(cart.total_price)) {
                    lastCart = cart;
                    updateThresholdElements(cart);
                  }
                });
              }
            } catch (err) {}
            return null;
          })
          .finally(() => queueRefresh(80));
      }
      return responsePromise;
    };
  };

  const hookXHR = () => {
    if (!window.XMLHttpRequest) return;
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
      this.__fs_url = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function send(...args) {
      this.addEventListener('load', () => {
        if (isCartRequest(this.__fs_url)) {
          try {
            if (this.responseText) {
              const cart = JSON.parse(this.responseText);
              if (cart && Number.isFinite(cart.total_price)) {
                lastCart = cart;
                updateThresholdElements(cart);
              }
            }
          } catch (err) {}
          queueRefresh(80);
        }
      });
      return originalSend.apply(this, args);
    };
  };

  const observeDom = () => {
    if (!('MutationObserver' in window)) return;
    const observer = new MutationObserver((mutations) => {
      let sawTarget = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(OBSERVE_SELECTOR) || node.querySelector(OBSERVE_SELECTOR)) {
            sawTarget = true;
            break;
          }
        }
        if (sawTarget) break;
      }
      if (sawTarget) {
        if (lastCart) {
          updateThresholdElements(lastCart);
        } else {
          queueRefresh(0);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    hookFetch();
    hookXHR();
    hookJqueryViewResponses();
    observeDom();
    queueRefresh(0);
    window.addEventListener('pageshow', () => queueRefresh(0));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
